import {
  completeBatchFiles,
  createUploadBatch,
  finishUploadBatch,
  initBatchFiles,
  type CreateBatchBody,
  type EvidenceLibraryLimits,
  type EvidenceUploadBatch,
  type LibraryFileMeta,
} from '../../request/evidence-library/evidenceLibraryRequests.ts';
import { uploadToGCS } from '../../utils/fileUpload';
import { sha256Hex, type PickedFile } from './folderFiles.ts';

/**
 * Runs one upload into the Evidence Library: start a batch, hash each file, ask the server where each goes
 * (or that it is a duplicate / rejected), PUT the bytes straight to storage, confirm, finish.
 * Requests go in chunks of `max_files_per_request`; PUTs and hashing run a few at a time.
 */

export type FileUploadState = 'queued' | 'hashing' | 'uploading' | 'uploaded' | 'duplicate' | 'skipped' | 'failed';

export type FileOutcome = {
  state: FileUploadState;
  reason?: string;
  uploadId?: number;
  evidenceId?: number;
  /** For a duplicate: the existing library file was added to the upload's control or run. */
  linked?: boolean;
};

export type UploadRunResult = {
  batch: EvidenceUploadBatch | null;
  outcomes: Record<string, FileOutcome>;
};

export type UploadRunOptions = {
  files: PickedFile[];
  batch: Omit<CreateBatchBody, 'files_expected' | 'bytes_expected'>;
  limits: EvidenceLibraryLimits;
  onProgress?: (outcomes: Record<string, FileOutcome>) => void;
  concurrency?: number;
};

/** Runs fn over items, at most `limit` at a time. */
export async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      await fn(item);
    }
  });
  await Promise.all(workers);
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

const TERMINAL: FileUploadState[] = ['uploaded', 'duplicate', 'skipped', 'failed'];

export function countOutcomes(outcomes: Record<string, FileOutcome>) {
  const c = { total: 0, done: 0, uploaded: 0, duplicate: 0, linked: 0, skipped: 0, failed: 0 };
  for (const o of Object.values(outcomes)) {
    c.total += 1;
    if (TERMINAL.includes(o.state)) c.done += 1;
    if (o.state === 'uploaded') c.uploaded += 1;
    if (o.state === 'duplicate' && o.linked) c.linked += 1;
    else if (o.state === 'duplicate') c.duplicate += 1;
    if (o.state === 'skipped') c.skipped += 1;
    if (o.state === 'failed') c.failed += 1;
  }
  return c;
}

export async function runLibraryUpload({ files, batch, limits, onProgress, concurrency = 4 }: UploadRunOptions): Promise<UploadRunResult> {
  const outcomes: Record<string, FileOutcome> = {};
  files.forEach((f) => { outcomes[f.id] = { state: 'queued' }; });
  const set = (id: string, o: FileOutcome) => {
    outcomes[id] = o;
    onProgress?.({ ...outcomes });
  };
  onProgress?.({ ...outcomes });

  const bytes = files.reduce((s, f) => s + f.file.size, 0);
  const created = await createUploadBatch({ ...batch, files_expected: files.length, bytes_expected: bytes });

  const metaById: Record<string, LibraryFileMeta> = {};
  // Files that failed before the server saw them complete (read, init, PUT); a const object so the loop's callbacks may count.
  const local = { failures: 0 };

  for (const group of chunk(files, limits.max_files_per_request || 50)) {
    // 1. Hash
    await runPool(group, concurrency, async (f) => {
      set(f.id, { state: 'hashing' });
      try {
        metaById[f.id] = {
          client_id: f.id,
          filename: f.file.name,
          relative_path: f.path,
          size: f.file.size,
          content_type: f.file.type || 'application/octet-stream',
          sha256: await sha256Hex(f.file),
        };
      } catch {
        local.failures += 1;
        set(f.id, { state: 'failed', reason: 'Could not read the file' });
      }
    });
    const metas = group.map((f) => metaById[f.id]).filter(Boolean);
    if (!metas.length) continue;

    // 2. Init: where to upload, or why not
    let inits;
    try {
      inits = await initBatchFiles(created.id, metas);
    } catch (e) {
      metas.forEach((m) => { local.failures += 1; set(m.client_id, { state: 'failed', reason: 'Server did not accept the files' }); });
      continue;
    }

    // A file the server left out of its reply would wait forever: end it as failed (it can be retried).
    const answeredInit = new Set(inits.map((r) => r.client_id));
    metas.filter((m) => !answeredInit.has(m.client_id)).forEach((m) => {
      local.failures += 1;
      set(m.client_id, { state: 'failed', reason: 'No answer from the server' });
    });

    // 3. PUT the bytes
    const toComplete: LibraryFileMeta[] = [];
    const byId = Object.fromEntries(group.map((f) => [f.id, f]));
    await runPool(inits, concurrency, async (r) => {
      if (r.status === 'duplicate') {
        set(r.client_id, { state: 'duplicate', reason: r.reason, uploadId: r.existing_upload_id, evidenceId: r.evidence_id, linked: r.linked });
        return;
      }
      if (r.status === 'rejected') {
        set(r.client_id, { state: 'skipped', reason: r.reason });
        return;
      }
      set(r.client_id, { state: 'uploading' });
      try {
        await uploadToGCS(r.upload_url, byId[r.client_id].file, r.content_type || metaById[r.client_id].content_type);
        toComplete.push({ ...metaById[r.client_id], object_key: r.object_key, content_type: r.content_type || metaById[r.client_id].content_type });
      } catch {
        local.failures += 1;
        set(r.client_id, { state: 'failed', reason: 'Upload to storage failed' });
      }
    });

    // 4. Confirm
    if (toComplete.length) {
      try {
        const done = await completeBatchFiles(created.id, toComplete);
        const answered = new Set(done.map((d) => d.client_id));
        toComplete.filter((m) => !answered.has(m.client_id)).forEach((m) => {
          local.failures += 1;
          set(m.client_id, { state: 'failed', reason: 'No answer from the server' });
        });
        done.forEach((d) => {
          set(d.client_id, {
            state: d.status === 'uploaded' ? 'uploaded' : d.status === 'duplicate' ? 'duplicate' : 'failed',
            reason: d.reason,
            uploadId: d.upload_id,
            evidenceId: d.evidence_id,
            linked: d.linked,
          });
        });
      } catch {
        toComplete.forEach((m) => { local.failures += 1; set(m.client_id, { state: 'failed', reason: 'Could not confirm the upload' }); });
      }
    }
  }

  let finished: EvidenceUploadBatch | null = created;
  try {
    finished = await finishUploadBatch(created.id, local.failures);
  } catch {
    // The files are saved; only the batch summary is missing.
  }
  return { batch: finished, outcomes };
}
